/**
 * useUserSearch hook helper
 * Interface with user profile REST API
 *
 * Author: Relevant Bits
 */

import { useContext } from 'react';

import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import z from 'zod';

import { getAxiosInstance } from './axiosCache';
import { ConfigContext } from '../aurr/contexts/ConfigContext';
import { cacheTTLs } from '../common/constants';
import { ApiRequestProps } from '../common/interface';

const resultSchema = z.array(
  z.object({
    associationType: z.string(),
    contentId: z.string(),
    personId: z.string(),
  })
);

const associationsSchema = z.object({
  result: resultSchema,
});

type ResultSchema = z.infer<typeof resultSchema>;

interface UseSpeakerAssociationsProps extends ApiRequestProps {
  contentId: string;
}

export const useSpeakerAssociations = ({
  contentId,
  baseURL,
  enabled = true,
  bypassCache = false,
}: UseSpeakerAssociationsProps) => {
  const { USER_CONTENT_URL } = useContext(ConfigContext);
  // create axios client used in all community function requests
  const axiosConfig = {
    baseURL: baseURL || USER_CONTENT_URL,
    ...(!bypassCache && {
      cache: {
        ttl: cacheTTLs.userProfile,
      },
    }),
  };

  const api: AxiosInstance = getAxiosInstance(bypassCache);

  const {
    isLoading,
    error,
    data = [],
  } = useQuery({
    queryKey: [`usercontent-associations-${contentId}`],
    queryFn: () =>
      api
        .get(
          `/usercontent/v1/associations?filter.contentId=${contentId}`,
          axiosConfig
        )
        .then(({ data: responseData }) => {
          const { result } = associationsSchema.parse(responseData);

          // Sort to ensure expert is first
          return resultSchema.parse(
            result.sort((a, b) => {
              if (a.associationType.toLowerCase() === 'primary speaker')
                return -1;
              if (b.associationType.toLowerCase() === 'primary speaker')
                return 1;
              if (a.associationType.toLowerCase() === 'au speaker') return -1;
              if (b.associationType.toLowerCase() === 'au speaker') return 1;
              if (a.associationType.toLowerCase() === 'expert') return -1;
              if (b.associationType.toLowerCase() === 'expert') return 1;
              return 0;
            })
          );
        }),
    enabled,
  });

  return {
    isLoading,
    error,
    data: data as ResultSchema,
  };
};
